<template>
  <div
    :class="tagClasses"
    :aria-label="props.ariaLabel"
    role="status"
    data-cy="ds-tag"
  >
    <DSUserAvatar
      v-if="props.avatarUrl"
      :src="props.avatarUrl"
      :size="DS_TAG_AVATAR_SIZE"
      :status="DS_TAG_AVATAR_STATUS"
      icon-name="user"
    />
    <DSIcon
      v-else-if="props.prefixIcon"
      :icon="props.prefixIcon"
      size="sm"
    />

    <DSPaletteItem
      v-else-if="props.swatch"
      :surface="props.swatch"
      :size="DS_PALETTE_ITEM_SIZES.ExtraSmall"
    />

    <span
      :class="DS_TAG_CLASSES.Text"
      data-cy="ds-tag__text"
    >
      {{ props.text }}
    </span>

    <DSBadge
      v-if="props.badgeText"
      :text="props.badgeText"
      :disabled="props.disabled"
      state="neutral"
      data-cy="ds-tag__badge"
    />

    <DSButton
      v-if="props.removable"
      :class="DS_TAG_CLASSES.RemoveButton"
      variant="ghost"
      size="xs"
      :aria-label="removeButtonAriaLabel"
      :disabled="props.disabled"
      type="button"
      data-cy="ds-tag__remove-button"
      @click="handleRemove"
    >
      <DSIcon
        icon="cross-small"
        size="sm"
        aria-hidden="true"
        data-cy="ds-tag__action-icon"
      />
    </DSButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { DSTagProps } from "@/src/design_system/data_display/tag/types";
import {
  DS_TAG_AVATAR_SIZE,
  DS_TAG_AVATAR_STATUS,
  DS_TAG_CLASSES,
  DS_TAG_COLOR_PALETTE_TO_CLASS_MAP,
} from "@/src/design_system/data_display/tag/constants";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSBadge from "@/src/design_system/data_display/badge/components/DSBadge.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import DSUserAvatar from "@/src/design_system/data_display/user/components/DSUserAvatar.vue";
import DSPaletteItem from "@/src/design_system/data_display/palette_item/components/DSPaletteItem.vue";
import { DS_PALETTE_ITEM_SIZES } from "@/src/design_system/data_display/palette_item/types";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";

const props = withDefaults(defineProps<DSTagProps>(), {
  colorPalette: undefined,
  disabled: false,
  prefixIcon: undefined,
  swatch: undefined,
  avatarUrl: undefined,
  removable: false,
  badgeText: undefined,
  ariaLabel: undefined,
});

const emit = defineEmits<{
  (e: "remove"): void;
}>();

const { stateClasses } = useStateClasses(props);

const tagClasses = computed(() => {
  return [DS_TAG_CLASSES.Tag, tagColorPaletteClass.value, stateClasses.value];
});

const tagColorPaletteClass = computed(() => {
  if (props.colorPalette) return DS_TAG_COLOR_PALETTE_TO_CLASS_MAP[props.colorPalette];

  return null;
});

const removeButtonAriaLabel = computed(() => {
  return window.I18n.designSystem.tag.ariaLabelRemove.replace("%{text}", () => props.text);
});

const handleRemove = (e: Event) => {
  e.stopPropagation();
  if (!props.disabled) {
    emit("remove");
  }
};
</script>
